import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Embed, models, Report } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import SyncIcon from "@mui/icons-material/Sync";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { useRecoilValue } from "recoil";
import { PowerBIReport } from "../../../api-client-nswag/taxportal-client";
import { taxPortalClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import UnAuthPowerBi from "../../../cui/commonComponents/UnAuthPowerBi";
import { userPermissions } from "../../../App";
import SiteNameDropdown from "./SiteNameDropdown";
import { useSnackbar } from "../../../hooks";
import "../PowerBIReport.scss";

export interface CommonPowerBIPageProps {
  /** When omitted the component reads ?reportType= from the URL. */
  reportType?: PowerBIReport;
  /** When omitted the component reads ?displayName= from the URL. */
  displayName?: string;
  siteFilterTarget?: { table: string; column: string };
  syncRefreshUrlBase?: string;
  syncMessage?: string;
}

interface SiteOption {
  value: number;
  label: string | undefined;
}

interface SiteNameState {
  pending: boolean;
  data: { id: number; sitename?: string }[];
}
interface SiteFilterTarget {
  table: string;
  column: string;
}

// Per-report site filter targets — column names differ across Power BI datasets.
const REPORT_SITE_FILTER_TARGETS: Partial<Record<PowerBIReport, SiteFilterTarget>> = {
  [PowerBIReport.CS_GLOBAL_DIRECT]:          { table: "SiteIDs", column: "Siteid" },
  [PowerBIReport.CS_US_DIRECT]:              { table: "SiteIDs", column: "Siteid" },
  [PowerBIReport.CS_EFILING_TRACKER]:        { table: "SiteIDs", column: "Siteid" },
  [PowerBIReport.CS_PROJECT_TRACKER_FULL]:   { table: "SiteIDs", column: "Siteid" },
  [PowerBIReport.CS_CASH_TAX]:               { table: "SiteIDs", column: "SiteID" },
  [PowerBIReport.CS_PLANNING_IDEAS]:         { table: "SiteIDs", column: "SiteId" },
  [PowerBIReport.CS_PROJECT_TRACKER_LITE]:   { table: "SiteIDs", column: "SiteId" },
};

const DEFAULT_SITE_FILTER_TARGET: SiteFilterTarget = { table: "SiteIDs", column: "SiteId" };

const resolveSiteFilterTarget = (
  reportType: PowerBIReport | undefined,
  providedTarget?: SiteFilterTarget,
): SiteFilterTarget => {
  if (providedTarget) return providedTarget;
  if (reportType !== undefined && REPORT_SITE_FILTER_TARGETS[reportType]) {
    return REPORT_SITE_FILTER_TARGETS[reportType]!;
  }
  return DEFAULT_SITE_FILTER_TARGET;
};

const buildSiteFilter = (table: string, column: string, siteId: number): models.IBasicFilter => ({
  $schema: "http://powerbi.com/product/schema#basic",
  target: { table, column },
  filterType: models.FilterType.Basic,
  operator: "In",
  values: [`${siteId}`],
  requireSingleSelection: true,
});

export const CommonPowerBIPage: React.FC<CommonPowerBIPageProps> = ({
  reportType: reportTypeProp,
  displayName: displayNameProp,
  siteFilterTarget,
  syncRefreshUrlBase,
  syncMessage,
}) => {
  const [searchParams] = useSearchParams();
  // ?reportType=109 — numeric enum value (e.g. CS_GLOBAL_DIRECT = 109)
  const reportType: PowerBIReport | undefined = reportTypeProp
    ?? (() => {
      const raw = parseInt(searchParams.get("reportType") ?? "", 10);
      return isNaN(raw) ? undefined : (raw as PowerBIReport);
    })();
  const displayName = displayNameProp ?? searchParams.get("displayName") ?? "Power BI Report";
  const effectiveSiteFilterTarget = resolveSiteFilterTarget(reportType, siteFilterTarget);
  const { user } = useContext(OidcIdentityContext);
  const userPermission = useRecoilValue(userPermissions);
  const { showSnackbar, showSuccessSnackbar } = useSnackbar();

  // Prefer siteID from URL param, fall back to sessionStorage.
  // Reading from URL directly avoids a race condition where App.tsx's useEffect
  // hasn't yet written the URL's siteID to sessionStorage by the time this component mounts.
  const urlSiteIdRaw = parseInt(searchParams.get("siteID") ?? "", 10);
  const sessionSiteIdRaw = parseInt(sessionStorage.getItem("siteId") ?? "", 10);
  const resolvedSiteId = (!isNaN(urlSiteIdRaw) && urlSiteIdRaw > 0)
    ? urlSiteIdRaw
    : (!isNaN(sessionSiteIdRaw) && sessionSiteIdRaw > 0)
    ? sessionSiteIdRaw
    : null;
  const lockedSiteId = resolvedSiteId;

  const [siteId, setSiteId] = useState<number | null>(lockedSiteId);

  const [selectedOption, setSelectedOption] = useState<SiteOption | null>(null);
  const [siteNames, setSiteNames] = useState<SiteNameState>({ pending: true, data: [] });

  const [report, setReport] = useState<Report | null>(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [embedReady, setEmbedReady] = useState(false);

  const [reportConfig, setReportConfig] = useState<models.IReportEmbedConfiguration>({
    type: "report",
    embedUrl: "",
    tokenType: models.TokenType.Embed,
    accessToken: "",
    settings: undefined,
  });

  const tokenExpirationRef = useRef<Date | null>(null);
  const tokenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const username = user?.profile?.preferred_username ?? "";


  // Site dropdown (only when not locked to a sessionStorage siteId)
  useEffect(() => {
    if (lockedSiteId) return; // dropdown not needed
    if (!username) return;

    (async () => {
      setSiteNames({ pending: true, data: [] });
      try {
        const data = await taxPortalClientApi.highQ_GetUserSites(username);
        setSiteNames({ pending: false, data: data as any[] });
      } catch {
        setSiteNames({ pending: false, data: [] });
      }
    })();
  }, [username, lockedSiteId]);

  const handleSiteChange = (option: SiteOption | null) => {
    setSelectedOption(option);
    setSiteId(option?.value ?? null);
    // Reset embed state when site changes
    setEmbedReady(false);
    setReport(null);
  };

  useEffect(() => {
    if (!siteId || reportType === undefined) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await taxPortalClientApi.reports_GetConsolidatedPowerBiEmbedUrl(undefined, reportType);
        if (cancelled) return;
        const expiration = new Date(res.expiration as Date);
        expiration.setMinutes(expiration.getMinutes() - 2);
        tokenExpirationRef.current = expiration;

        setReportConfig({
          type: "report",
          embedUrl: res.embedUrl ?? "",
          tokenType: models.TokenType.Embed,
          accessToken: res.token ?? "",
          settings: {
            filterPaneEnabled: false,
            navContentPaneEnabled: false,
            layoutType: models.LayoutType.Custom,
            customLayout: {
              displayOption: models.DisplayOption.FitToWidth,
            },
          },
        });

        setIsUnauthorized(false);
        setEmbedReady(true);
        scheduleTokenRefresh(expiration);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 401) {
          setIsUnauthorized(true);
        } else {
          showSnackbar({
            message: `Failed to load ${displayName} report.`,
            severity: "error",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, reportType]);

  useEffect(() => {
    if (!report || !siteId || !embedReady) return;

    const siteFilter = buildSiteFilter(
      effectiveSiteFilterTarget.table,
      effectiveSiteFilterTarget.column,
      siteId,
    );

    // Guards against the loop: setFilters → rendered → setFilters → ...
    let isReapplying = false;

    const onLoaded = () => {
      // Reset any bookmark that may override filters, then unconditionally apply the site filter.
      (report as any).bookmarksManager?.apply("Reset").catch(() => {});
      report
        .setFilters([siteFilter])
        .catch((err: any) =>
          console.warn(`[CommonPowerBIPage] Failed to apply site filter for ${displayName}:`, err),
        );
    };

    // Re-apply the site filter if the user clears filters via the Power BI toolbar.
    const onRendered = async () => {
      if (isReapplying) return;
      try {
        const current = (await report.getFilters()) ?? [];
        const hasSiteFilter = current.some((f) => {
          const basic = f as models.IBasicFilter;
          const target = basic.target as { table?: string; column?: string };
          return (
            target?.table === effectiveSiteFilterTarget.table &&
            target?.column === effectiveSiteFilterTarget.column &&
            (basic.values ?? []).map(String).includes(String(siteId))
          );
        });
        if (!hasSiteFilter) {
          isReapplying = true;
          await report.setFilters([siteFilter]);
        }
      } catch {
        // Ignore — report may not be ready
      } finally {
        isReapplying = false;
      }
    };

    report.off("loaded");
    report.off("rendered");
    report.on("loaded", onLoaded);
    report.on("rendered", onRendered);
    report.refresh();

    return () => {
      report.off("loaded");
      report.off("rendered");
    };
  }, [report, siteId, embedReady, effectiveSiteFilterTarget, displayName]);

  const scheduleTokenRefresh = useCallback(
    (expiration: Date) => {
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }

      const refreshIn = expiration.getTime() - Date.now() - 2 * 60 * 1000;
      if (refreshIn <= 0) return;

      tokenTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await taxPortalClientApi.reports_GetEmbedReport(reportType, userPermission);
          if (report && res?.token) {
            await report.setAccessToken(res.token);
            const newExpiration = new Date();
            newExpiration.setHours(newExpiration.getHours() + 1);
            tokenExpirationRef.current = newExpiration;
            scheduleTokenRefresh(newExpiration);
          }
        } catch {
          console.warn(`[CommonPowerBIPage] Token refresh failed for ${displayName}.`);
        }
      }, refreshIn);
    },
    [report, reportType, displayName],
  );

  useEffect(() => {
    return () => {
      if (tokenTimeoutRef.current) clearTimeout(tokenTimeoutRef.current);
    };
  }, []);

  const handleSync = async () => {
    if (!siteId || !syncRefreshUrlBase || reportType === undefined) return;
    try {
      const refreshResponse = await taxPortalClientApi.reports_GetCSPowerBIRefreshURL(siteId, username, reportType);
      if (refreshResponse?.isSuccess === true) {
        showSuccessSnackbar({
          message: refreshResponse.message ?? syncMessage ?? `${displayName} data synchronization in progress`,
        });
      } else {
        showSnackbar({
          message: refreshResponse?.message ?? "Something went wrong!",
          severity: "error",
        });
      }
    } catch (err) {
      console.error(`[CommonPowerBIPage] Sync failed for ${displayName}:`, err);
    }

    if (report) {
      setTimeout(() => report.refresh(), 2000);
    }
  };

  const dropdownOptions = siteNames.data.map((s: any) => ({
    value: s.id,
    label: s.sitename,
  }));

  return (
    <>
      {!lockedSiteId && (
        <div
          style={{
            display: "flex",
            margin: "16px 0 16px 16px",
            justifyContent: "space-between",
          }}
        >
          <SiteNameDropdown
            options={dropdownOptions}
            handleOnChange={handleSiteChange}
            siteName={siteNames}
            selectedOption={selectedOption}
          />
        </div>
      )}

      <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          {isUnauthorized ? (
            <UnAuthPowerBi name={displayName} />
          ) : (
            <div style={{ position: "relative", width: "100%" }}>
              <PowerBIEmbed
                embedConfig={reportConfig}
                cssClassName="report-style-class"
                getEmbeddedComponent={(embedObject: Embed) => {
                  setReport(embedObject as Report);
                }}
              />

              {syncRefreshUrlBase && reportConfig.settings !== undefined && (
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 40,
                    cursor: "pointer",
                  }}
                >
                  <SyncIcon
                    onClick={handleSync}
                    style={{ color: "#009cde", fontSize: "35px" }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommonPowerBIPage;
